import { useFormikContext } from 'formik';
import { isEqual, omit } from 'lodash';
import { useEffect, useRef } from 'react';

import { useDebounce } from '@hooks/useDebounce';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';

const storage = window.sessionStorage;

/**
 * Formik form persistance.
 * Used as a drop-in component for a form to allow saving the form values in
 * a defined storage, allowing for state to persist, e.g. over page reloads.
 *
 * Usage:
 * ```
 * <Formik>
 *   <form>
 *     <FormikPersist name="someForm" />
 *     <input ... />
 *     ...
 *   </form>
 * <Formik>
 * ```
 *
 * @param {string} name - unique identifier for the form, used as a key for storage
 * @param {string[]} exclude - field names to omit from storage (e.g. passwords, payment details)
 * @param {FormValues} committedValues - the source-of-truth values for the form.
 *   When provided, storage stays in sync with this prop instead of live Formik
 *   values, so unsaved edits are never persisted.
 * @param {boolean} suspend - when true, pauses all syncing (restoring, committing,
 *   and autosaving) until it's false again, e.g. while submitting
 */
const FormikPersist = <FormValues,>({
  name,
  exclude = [],
  committedValues,
  suspend = false,
}: {
  name: string;
  exclude?: string[];
  committedValues?: FormValues;
  suspend?: boolean;
}) => {
  const { values, setValues } = useFormikContext<FormValues>();
  const prevValuesRef = useRef<FormValues>(values);
  const { canUpdateState } = useAppointmentContext();

  const debouncedOnSave = useDebounce((values: FormValues) => {
    if (!canUpdateState) return;
    storage.setItem(name, JSON.stringify(omit(values as object, exclude)));
  }, 300);

  useEffect(() => {
    return () => {
      debouncedOnSave.cancel();
    };
  }, [debouncedOnSave]);

  useEffect(() => {
    if (suspend) return;

    if (committedValues) {
      debouncedOnSave.cancel();
      if (!isEqual(prevValuesRef.current, committedValues)) {
        prevValuesRef.current = committedValues;
        setValues(committedValues);
      }
      return;
    }
    // restore saved form values from store and apply them
    // as the current Formik values
    const savedForm = storage.getItem(name);

    if (savedForm) {
      const parsedForm = JSON.parse(savedForm);

      prevValuesRef.current = parsedForm;
      setValues(parsedForm);
    }
  }, [name, setValues, committedValues, suspend, debouncedOnSave]);

  // save current Formik values to store, if different
  // comparison prevents spamming storage on re-render
  useEffect(() => {
    if (suspend || committedValues) return;
    if (!isEqual(prevValuesRef.current, values)) {
      debouncedOnSave(values);
    }
  });

  // save the values in react state, so they can be
  // used later to compare to new values and determine
  // whether to update in storage
  useEffect(() => {
    if (suspend || committedValues) return;
    prevValuesRef.current = values;
  }, [values, committedValues, suspend]);

  return null;
};

export { FormikPersist };
